// The full custom application shell from the "IDP Redesign" mock: a bespoke
// left sidebar (923 logo / Platform · self-service / nav / user footer) and a
// compact top bar (breadcrumb / Docs / notifications / owner pill) wrapping the
// routed page content. It replaces Backstage's default SidebarPage chrome via
// the app/layout override in ./layout.tsx.
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  googleAuthApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { GlobalStyles, Icon, color, font, type IconName } from '../design';
import { entityNameFromRef } from '../sandbox/model';
import { useAccessApi } from '../access/api';
import logo from '../auth/ninetwothree-logo.jpeg';

type NavDef = { id: string; label: string; icon: IconName; path: string };

const NAV: NavDef[] = [
  { id: 'infra', label: 'Infrastructure Request', icon: 'cloud', path: '/' },
  { id: 'catalog', label: 'Catalog', icon: 'squares-four', path: '/catalog' },
];

// Only rendered for portal admins (GET /access/me).
const ADMIN_NAV: NavDef[] = [
  { id: 'access', label: 'Access', icon: 'users', path: '/access' },
  { id: 'third-party', label: 'Third-Party Access', icon: 'plugs', path: '/third-party-access' },
  { id: 'platform', label: 'Platform', icon: 'gear', path: '/platform' },
];

const isActive = (path: string, pathname: string) =>
  path === '/' ? pathname === '/' : pathname.startsWith(path);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('') || 'U';

export const AppShell = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const identityApi = useApi(identityApiRef);
  const googleAuthApi = useApi(googleAuthApiRef);
  const accessApi = useAccessApi();
  const [displayName, setDisplayName] = useState('User');
  const [isAdmin, setIsAdmin] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const [identity, profile] = await Promise.all([
          identityApi.getBackstageIdentity(),
          googleAuthApi.getProfile({ optional: true }),
        ]);
        if (!mounted) return;
        setDisplayName(
          profile?.displayName || entityNameFromRef(identity.userEntityRef),
        );
        // Register this user in the portal's user directory so admins can
        // share infrastructure with them from the Access page.
        void accessApi
          .recordProfile({
            displayName: profile?.displayName,
            email: profile?.email,
          })
          .catch(() => undefined);
      } catch {
        // Non-fatal: the shell just shows a generic name.
      }
    })();
    void accessApi
      .me()
      .then(me => {
        if (mounted) setIsAdmin(me.isAdmin);
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, [identityApi, googleAuthApi, accessApi]);

  const navItems = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;
  const active = navItems.find(n => isActive(n.path, pathname)) ?? NAV[0];

  return (
    <div
      className="ntt-scope"
      style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: color.bg }}
    >
      <GlobalStyles />

      {/* ---------------- Sidebar ---------------- */}
      <aside
        className="ntt-shell-aside"
        style={{
          width: 236,
          flex: 'none',
          background: color.bgSidebar,
          borderRight: `1px solid ${color.borderSoft}`,
          display: 'flex',
          flexDirection: 'column',
          padding: '14px 12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 16px' }}>
          <img
            src={logo}
            alt="NineTwoThree"
            width={30}
            height={30}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              objectFit: 'cover',
              boxShadow: `0 0 0 1px ${color.border}`,
            }}
          />
          <div className="ntt-shell-hide" style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: color.textBright }}>NineTwoThree</div>
            <div style={{ fontSize: 10, color: color.textDimmest }}>
              internal development platform
            </div>
          </div>
        </div>

        <div className="ntt-shell-hide" style={{ position: 'relative', margin: '0 4px 14px' }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
            <Icon name="magnifying-glass" size={13} color={color.textPlaceholder} />
          </span>
          <input
            placeholder="Search  /"
            style={{
              width: '100%',
              background: '#111319',
              border: `1px solid ${color.border}`,
              borderRadius: 7,
              padding: '7px 9px 7px 28px',
              color: color.textSecondary,
              fontSize: 12.5,
            }}
          />
        </div>

        <div
          className="ntt-shell-hide"
          style={{
            font: `600 10px ${font.mono}`,
            letterSpacing: '.12em',
            color: '#4c545f',
            padding: '2px 10px 7px',
          }}
        >
          PLATFORM
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map(n => {
            const on = isActive(n.path, pathname);
            return (
              <a
                key={n.id}
                href={n.path}
                title={n.label}
                className="ntt-shell-navitem"
                onClick={e => {
                  e.preventDefault();
                  navigate(n.path);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '8px 10px',
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: on ? color.textBright : color.textFaint,
                  background: on ? 'color-mix(in oklab, #7b74ff 16%, transparent)' : 'transparent',
                  fontWeight: on ? 600 : 500,
                  boxShadow: on ? `inset 2px 0 0 ${color.accent}` : 'none',
                }}
              >
                <Icon name={n.icon} size={16} style={{ width: 18 }} />
                <span className="ntt-shell-hide">{n.label}</span>
              </a>
            );
          })}
        </nav>

        <div
          className="ntt-shell-footer"
          style={{
            marginTop: 'auto',
            borderTop: `1px solid ${color.borderSoft}`,
            paddingTop: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            paddingLeft: 6,
            paddingRight: 6,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#3a3f6b,#2a2d3d)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: `600 11px ${font.mono}`,
              color: '#c9c6ff',
            }}
          >
            {initials(displayName)}
          </div>
          <div className="ntt-shell-hide" style={{ lineHeight: 1.15, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: color.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            <div style={{ fontSize: 10, color: color.textDimmest, fontFamily: font.mono }}>Engineering</div>
          </div>
          <button
            type="button"
            title="Sign out"
            aria-label="Sign out"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void identityApi.signOut().catch(() => setSigningOut(false));
            }}
            style={{
              marginLeft: 'auto',
              flex: 'none',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: `1px solid ${color.border}`,
              borderRadius: 7,
              color: color.textFaint,
              cursor: signingOut ? 'default' : 'pointer',
            }}
          >
            <Icon name={signingOut ? 'spinner' : 'logout'} size={15} spin={signingOut} />
          </button>
        </div>
      </aside>

      {/* ---------------- Main column ---------------- */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <header
          style={{
            height: 52,
            flex: 'none',
            borderBottom: `1px solid ${color.borderSoft}`,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '0 20px',
            background: 'rgba(12,14,19,.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: color.textDimmer }}>
            <Icon name={active.icon} size={15} color={color.accentLight} />
            <span style={{ color: color.text, fontWeight: 500 }}>{active.label}</span>
          </div>
        </header>

        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '26px 28px 80px' }}>{children}</div>
        </div>
      </div>
    </div>
  );
};
